import {AgGridReact} from "ag-grid-react";
import React, {useCallback, useEffect, useRef, useState} from "react";
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-balham.css';
import './DiagnosticStyles.css';
import {cellStylePercentage, PercentageCell, calculateRoundPercent} from './Utils.js';
import {
    ChartLineColor,
    COL_DEFS,
    COL_WIDTH,
    COMPARISON_TYPE,
    defaultPDFParams,
    GRID_ENUMS,
    GridStyle
} from "./Styles";
import LineChartLegend from "./LineChartLegend";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import {printDoc} from "./pdfExport/printDoc";
import CircularLoader from "@common/CircularLoader";
import {default as ChTooltip} from "@common/Tooltip";
import IconButton from "@common/IconButton";
import {TEST_GROUP_TEST_TYPES} from "../tests/teacher/Diagnostics/Reports/TestGroupReports";
import {
    LineChart,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    Line,
    ResponsiveContainer, Dot
} from 'recharts';
import Card from "@common/Card";


let tooltipVals,
    lineColMap;


const ComparisonView = props => {
    const {isLoading, setLoading, testAssignmentsData, students, studentIds, selectedTestIds, classId,
            selectedDifficultyIds, textValues, curriculumValues, comparisonContentTypeVal, showAverageLine, showTrendLines,
            openDialogCallback, triggerGenerate,
            isTestGroup = false} = props,
        preTestFieldName = "preTestScore",
        preTestPercentFieldName = "preTestScorePercent",
        preTestMaxScoreFieldName = "preTestMaxScore",
        postTestFieldName = "postTestScore",
        postTestPercentFieldName = "postTestScorePercent",
        postTestMaxScoreFieldName = "postTestMaxScore",
        preTest = 'Pre/Mid-test(s)',
        postTest = 'Post-test',
        rowHeight = 30,
        headerHeight = 32,
        datasetMeanId = 0,
        minAndMaxId = -1,
        REPORT_NAME = 'ComparisonViewReport',
        [lineColArr,setLineColArr] = useState([]),
        [lineColMean,setLineColMean] = useState([]);




    const StudentCustomCell = props => {
        const {data, value} = props;
        const onClick = () => {
            if (typeof data.callback === 'function') {
                data.callback(data.studentId);
            }
        }

        return (
            <div className={'custom-cell-cont'}>
                <ChTooltip title={value} placement="left" enterNextDelay={100}>
                    <div className={'label'}>{value}</div>
                </ChTooltip>
                <IconButton onClick={onClick} tooltip={'Show Student Test/s'} placement={'left'}>
                    <span className="material-icons">open_in_new</span>
                </IconButton>
            </div>
        )
    }

    const columnDefs = [
        {
            flex:1,
            headerName:'Student',
            headerClass: 'text-center-align',
            cellStyle:{
                textAlign:'left',
                borderRight:'1px solid lightgrey'
            },
            field:'name',
            sortable:true,
            unSortIcon: true,
            wrapText:true,
            cellRenderer: StudentCustomCell
        },
        {
            flex:1,
            sortable: true,
            unSortIcon: true,
            headerName: preTest,
            field: preTestPercentFieldName,
            headerClass: 'text-center-align',
            cellStyle: cellStylePercentage,
            cellRenderer: PercentageCell
        },
        {
            flex:1,
            sortable: true,
            unSortIcon: true,
            headerName: postTest,
            field: postTestPercentFieldName,
            headerClass: 'text-center-align',
            cellStyle: cellStylePercentage,
            cellRenderer: PercentageCell
        }
    ];

    const gridRef = useRef(),
        [graphData,setGraphData] = useState(null),
        [chartData,setChartData] = useState([]),
        [rechartDataPoints,setRechartDataPoints] = useState([]),
        [studentSeriesMap,setStudentSeriesMap] = useState({}),
        [unfilteredData, setUnfilteredData] = useState([]),
        [unfilteredChartData, setUnfilteredChartData] = useState([]),
        [questionResults, setQuestionResults] = useState([]),
        [testResultsMap, setTestResultsMap] = useState({}),
        [adaptive, setAdaptive] = useState(false);

    const onGridReady = ()=>{
        showHideUserRows();
        setLoading(false);
        // gridRef.current.columnApi.autoSizeAllColumns();
    }

    const onChartReady = (plot)=>{
        showHideUserRows();
        setLoading(false);
        plot.render();
        // gridRef.current.columnApi.autoSizeAllColumns();
    }

    useEffect(()=>{
        showHideUserRows();
    }, [studentSeriesMap]);

    const showHideUserRows = ()=>{
        let selectedData = unfilteredData.filter(row => studentIds.includes(row.studentId)),
            selectedChartData = unfilteredChartData.filter(row => studentIds.includes(row.userId));

        calculateMean(selectedChartData);
        setGraphData(selectedData);
        setChartData(selectedChartData);
    }

    const calculateMean = dataArr =>{
        if (students.length === 0 || dataArr.length === 0 || Object.keys(studentSeriesMap).length === 0) {
            return;
        }

        let preTestTotal = 0,
            postTestTotal = 0,
            preTestCount = 0,
            postTestCount = 0,
            bestScore = 0;

        dataArr.forEach(row =>{
            if(row.test === preTest && studentSeriesMap[row.series].preTestAnswered){
                preTestTotal += row.value;
                preTestCount++;
            }else if(row.test === postTest && studentSeriesMap[row.series].postTestAnswered){
                postTestTotal += row.value;
                postTestCount++;
            }
            bestScore = Math.max(bestScore,Math.round(row.value));
        });
        if(preTestCount > 0){
            dataArr.push({
                userId: datasetMeanId,
                series: GRID_ENUMS.USER + datasetMeanId,
                name: 'Dataset mean',
                test: preTest,
                value: Math.round(preTestTotal / preTestCount),
                hoverDisplay: Math.round(preTestTotal / preTestCount) + '%',
                difference: (postTestTotal / preTestCount) - (preTestTotal / preTestCount),
                answered: false
            });
        }

        if(postTestCount > 0){
            dataArr.push({
                userId: datasetMeanId,
                series: GRID_ENUMS.USER + datasetMeanId,
                name: 'Dataset mean',
                test: postTest,
                value: Math.round(postTestTotal / postTestCount),
                hoverDisplay: Math.round(postTestTotal / postTestCount) + '%',
                difference: (postTestTotal / postTestCount) - (preTestTotal / postTestCount),
                answered: false
            });
        }
        // dataArr.push({userId: minAndMaxId, series: GRID_ENUMS.USER+minAndMaxId,  name:'min', test:preTest, value:0});
        // dataArr.push({userId: minAndMaxId, series: GRID_ENUMS.USER+minAndMaxId,  name:'max', test:postTest, value:Math.min(bestScore+10,100)});
    }

    const comparisonLegendData = [
        {colors:{
                background: ChartLineColor.MEAN,
                border:ChartLineColor.MEAN_BORDER
            },
            text:'Average score'},
        {colors:{
                background: ChartLineColor.DECLINE,
                border:ChartLineColor.DECLINE_BORDER
            },
            text:'Decline'},
        {colors:{
                background: ChartLineColor.INCLINE,
                border:ChartLineColor.INCLINE_BORDER
            },
            text:'Growth'},
        {colors:{
                background: ChartLineColor.STAGNANT,
                border:ChartLineColor.STAGNANT_BORDER
            },
            text:'Stagnant'}
    ];

    useEffect(()=>{
        showHideUserRows();
    },[studentIds]);

    useEffect(()=>{

        if(selectedTestIds === undefined || selectedTestIds.length === 0 || testAssignmentsData.length === 0){
            return;
        }

        setGraphData(null);
        setChartData(null);
        setLoading(true);

        if(testAssignmentsData.length > 0){
            let tempQuestionResults = [],
                tempTestResultsMap = {},
                isAdaptive = false;

            testAssignmentsData.forEach((testAssignmentData) => {
                if(!selectedTestIds.includes(testAssignmentData.testId)){
                    return;
                }
                let testType = testAssignmentData.testPurpose === 'DIAGNOSTIC_POST_TEST' ? TEST_GROUP_TEST_TYPES.POST_TEST : TEST_GROUP_TEST_TYPES.PRE_TEST;
                if(testAssignmentData.questionResultBeans != undefined) {
                    tempQuestionResults = [...tempQuestionResults, ...testAssignmentData.questionResultBeans];
                }

                testAssignmentData.testResultBeans.forEach((testResult) => {
                    if (!isAdaptive && testResult.adaptive) setAdaptive(true);
                    if (!tempTestResultsMap.hasOwnProperty(testResult.userId)) {
                        tempTestResultsMap[testResult.userId] = {preTest: null, postTest: null};
                    }

                    tempTestResultsMap[testResult.userId][testType] = testResult;
                });
            });

            setTestResultsMap(tempTestResultsMap);
            setQuestionResults(tempQuestionResults);
            setLoading(false);
        }else{
            setLoading(false);
        }
    },[triggerGenerate]);

    useEffect(()=>{
        if(questionResults.length > 0){
            setGraphData(null);
            setChartData(null);
            // setLoading(true);
            mapQuestionResults();
        }
    },[questionResults, selectedDifficultyIds, textValues, curriculumValues]);

    const mapIncludedContentIds = valuesArr =>{
        let map = {syllabusIds:[], topicIds:[], conceptIds:[]};
        for(const value of valuesArr){
            let split = value.split('-');
            for(let i = 0; i < split.length; i++){
                const valInt = parseInt(split[i]);
                const extraVal = isTestGroup ? 1 : 0;
                if(i === 0+extraVal && !map.syllabusIds.includes(valInt)){
                    map.syllabusIds.push(valInt);
                }else if(i === 1+extraVal && !map.topicIds.includes(valInt)) {
                    map.topicIds.push(valInt);
                }else if(i === 2+extraVal && !map.conceptIds.includes(valInt)){
                    map.conceptIds.push(valInt);
                }
            }
        }
        return map;
    }

    const mapQuestionResults = ()=>{
        let studentResults = {};
        let textIncludedMap = {syllabusIds:[], topicIds:[], conceptIds:[]};
        let curriculumIncludedMap = {syllabusIds:[], topicIds:[], conceptIds:[]};
        students.forEach(student=>{
            studentResults[student.userId] = {
                name:student.lastName+', '+student.firstName,
                preTest:{testMark:0, testMaxMark:0, testPercentVal:null, answered:false},
                postTest:{testMark:0, testMaxMark:0, testPercentVal:null, answered:false}
            };
        });

        if(textValues && textValues.length > 0){
            textIncludedMap = mapIncludedContentIds(textValues);
        }
        if(curriculumValues && curriculumValues.length > 0){
            curriculumIncludedMap = mapIncludedContentIds(curriculumValues);
        }

        if (adaptive) {
            questionResults.forEach((questionResult,idx) =>{
                if(selectedDifficultyIds.length > 0 && !selectedDifficultyIds.includes(questionResult.questionDiffLevel)){
                    return;
                }

                if (comparisonContentTypeVal === COMPARISON_TYPE.TEXT.val) {
                    if (!checkInFilter(textValues, {
                        syllabusId: questionResult.syllabusId,
                        topicId: questionResult.topicId,
                        conceptId: questionResult.conceptId
                    }, textIncludedMap)) {
                        return;
                    }
                } else if (comparisonContentTypeVal === COMPARISON_TYPE.CURRICULUM.val) {
                    if (!checkInFilter(curriculumValues, {
                        syllabusId: questionResult.curriculumSyllabusId,
                        topicId: questionResult.curriculumTopicId,
                        conceptId: questionResult.curriculumConceptId
                    }, curriculumIncludedMap)) {
                        return;
                    }
                }

                if(questionResult.testPurpose === 'DIAGNOSTIC_POST_TEST'){
                    addAdaptiveDataToStudent(studentResults, questionResult,TEST_GROUP_TEST_TYPES.POST_TEST);
                }else if(questionResult.testPurpose === 'DIAGNOSTIC_PRE_TEST'){
                    addAdaptiveDataToStudent(studentResults, questionResult,TEST_GROUP_TEST_TYPES.PRE_TEST);
                }
            });
        } else {
            let preTestFilteredTotalMark = 0,
                postTestFilteredTotalMark = 0,
                filteredQuestionIds = [];

            testAssignmentsData.forEach((testAssignmentData) => {
                testAssignmentData.testQuestionBeans.forEach((testQuestion) => {
                    if(selectedDifficultyIds.length > 0 && !selectedDifficultyIds.includes(testQuestion.testDiffLevel)){
                        return;
                    }

                    if (comparisonContentTypeVal === COMPARISON_TYPE.TEXT.val) {
                        if (testQuestion.concept === undefined || !checkInFilter(textValues, {
                            syllabusId: testQuestion.concept.syllabusId,
                            topicId: testQuestion.concept.topicId,
                            conceptId: testQuestion.concept.conceptId
                        }, textIncludedMap)) {
                            return;
                        }
                    } else if (comparisonContentTypeVal === COMPARISON_TYPE.CURRICULUM.val) {
                        if (testQuestion.curriculumConcept === undefined || !checkInFilter(curriculumValues, {
                            syllabusId: testQuestion.curriculumConcept.syllabusId,
                            topicId: testQuestion.curriculumConcept.topicId,
                            conceptId: testQuestion.curriculumConcept.conceptId
                        }, curriculumIncludedMap)) {
                            return;
                        }
                    }

                    if (!filteredQuestionIds.includes(testAssignmentData.testAssignmentId+''+testQuestion.questionId)) {
                        filteredQuestionIds.push(testAssignmentData.testAssignmentId+''+testQuestion.questionId);
                        if (testAssignmentData.testPurpose === 'DIAGNOSTIC_POST_TEST') {
                            postTestFilteredTotalMark += testQuestion.maxMark;
                        } else if (testAssignmentData.testPurpose === 'DIAGNOSTIC_PRE_TEST') {
                            preTestFilteredTotalMark += testQuestion.maxMark;
                        }
                    }
                });
            });

            questionResults.forEach((questionResult,idx) =>{
                if (filteredQuestionIds.includes(questionResult.testAssignmentId+''+questionResult.questionId)) {
                    if(questionResult.testPurpose === 'DIAGNOSTIC_POST_TEST'){
                        addDataToStudent(studentResults, questionResult, postTestFilteredTotalMark, TEST_GROUP_TEST_TYPES.POST_TEST);
                    }else if(questionResult.testPurpose === 'DIAGNOSTIC_PRE_TEST'){
                        addDataToStudent(studentResults, questionResult, preTestFilteredTotalMark, TEST_GROUP_TEST_TYPES.PRE_TEST);
                    }
                }
            });
        }

        let graphArr = [],
            chartArr = [],
            studentDifferenceMap = {};
        for (const [key, value] of Object.entries(studentResults)) {
            let rowData = {};
            rowData.studentId = parseInt(key);
            rowData.name = value.name;
            rowData.testIds = selectedTestIds;
            rowData.callback = openDialogCallback;

            let tablePrePercentVal,
                tablePostPercentVal,
                chartPrePercentVal = 0,
                chartPostPercentVal = 0,
                chartPreHoverDisplay = GRID_ENUMS.NOT_ATTEMPTED,
                chartPostHoverDisplay = GRID_ENUMS.NOT_ATTEMPTED;

            if (value.preTest.answered) {
                chartPrePercentVal = tablePrePercentVal = value.preTest.testPercentVal;
                chartPreHoverDisplay = value.preTest.testPercentVal + '%';
            }
            if (value.postTest.answered) {
                chartPostPercentVal = tablePostPercentVal = value.postTest.testPercentVal;
                chartPostHoverDisplay = value.postTest.testPercentVal + '%';
            }

            rowData[preTestPercentFieldName] = tablePrePercentVal;
            rowData[postTestPercentFieldName] = tablePostPercentVal;

            let differenceVal = 0,
                timeDifferenceVal = 0;
            studentDifferenceMap[GRID_ENUMS.USER + rowData.studentId] = {
                differenceVal: null,
                // timeDifferenceVal: null,
                preTestAnswered: value.preTest.answered,
                postTestAnswered: value.postTest.answered
            }
            if (!isNaN(rowData[postTestPercentFieldName]) && !isNaN(rowData[preTestPercentFieldName])) {
                differenceVal = Math.round(value.postTest.testPercentVal - value.preTest.testPercentVal);
                // timeDifferenceVal = value.postTest.timeTaken - value.preTest.timeTaken
                studentDifferenceMap[GRID_ENUMS.USER + rowData.studentId].differenceValue = differenceVal;
                // studentDifferenceMap[GRID_ENUMS.USER + rowData.studentId].timeDifferenceValue = timeDifferenceVal;
            }
            rowData.differenceVal = differenceVal;
            // rowData.growthPercentage = differenceVal+'%';
            rowData.preTestAnswered = value.preTest.answered;
            rowData.postTestAnswered = value.postTest.answered;

            chartArr.push({
                userId: rowData.studentId,
                series: GRID_ENUMS.USER + rowData.studentId,
                name: rowData.name,
                test: preTest,
                testType: TEST_GROUP_TEST_TYPES.PRE_TEST,
                value: chartPrePercentVal,
                difference: differenceVal,
                // timeDifference: timeDifferenceVal,
                testOrder: 0,
                hoverDisplay: chartPreHoverDisplay,
                answered: value.preTest.answered,
                // timeTaken:value.preTest.timeTaken,
                // label:rowData.initials
            });

            chartArr.push({
                userId: rowData.studentId,
                series: GRID_ENUMS.USER + rowData.studentId,
                name: rowData.name,
                test: postTest,
                testType: TEST_GROUP_TEST_TYPES.POST_TEST,
                value: chartPostPercentVal,
                difference: differenceVal,
                // timeDifference: timeDifferenceVal,
                testOrder: 1,
                hoverDisplay: chartPostHoverDisplay,
                answered: value.postTest.answered,
                // timeTaken:value.postTest.timeTaken,
                // label:rowData.initials
            });
            graphArr.push(rowData);
        }

        setStudentSeriesMap(studentDifferenceMap);
        setChartData(chartArr);
        setUnfilteredChartData(chartArr);
        setGraphData(graphArr);
        setUnfilteredData(graphArr);
    }

    useEffect(()=>{
        if(chartData && chartData.length > 0){

            setRechartDataPoints(processDataForLineChart());
        }
    },[chartData]);

    const checkInFilter = (values, questIds, includedMap)=>{
        let isIncluded = true;

        if(!includedMap.syllabusIds.includes(questIds.syllabusId)){
            isIncluded = false;
        }else if(includedMap.topicIds.length > 0 && !includedMap.topicIds.includes(questIds.topicId)){
            isIncluded = false;
        }else if(includedMap.conceptIds.length > 0 && !includedMap.conceptIds.includes(questIds.conceptId)){
            isIncluded = false;
        }
        return isIncluded;
    }

    const processDataForLineChart = ()=>{
        const dataPoints = [],
            preTests = [],
            postTests = [];

        lineColMap = new Map();
        tooltipVals = new Map();



        chartData.forEach((datum)=>{
            if(datum.userId !== minAndMaxId){
                if(datum.test === preTest){
                    preTests.push(datum);
                }else{
                    postTests.push(datum);
                }

            let color,
                lineWidth = 2;

            if(datum.series === GRID_ENUMS.USER+datasetMeanId){
                color = ChartLineColor.MEAN;
            }else if(datum.series === GRID_ENUMS.USER+minAndMaxId){
                lineWidth = 0;
            }else{
                if(!showTrendLines){
                    lineWidth = 0;
                }
                if(!isNaN(studentSeriesMap[datum.series].differenceValue)){
                    if(studentSeriesMap[datum.series].differenceValue > 0){
                        color = ChartLineColor.INCLINE;
                    }else if(studentSeriesMap[datum.series].differenceValue < 0){
                        color = ChartLineColor.DECLINE;
                    }else{
                        color = ChartLineColor.STAGNANT;
                    }
                }else{
                    lineWidth = 0;
                }
            }
                lineColMap.set(datum.series, {lineWidth:lineWidth, stroke:color, fill:color, data:datum });
            }
        });

        const preTestObj = {testType:'preTest',test:preTest},
            postTestObj = {testType:'postTest', test:postTest};
        for(let i = 0; i < preTests.length; i++){
            const preTest = preTests[i];
            preTestObj[preTest.series] = preTest.value;
            tooltipVals.set(preTest.series+'preTest',{value: preTest.value, name:preTest.name, hoverDisplay: preTest.hoverDisplay, answered: preTest.answered})
        }

        for(let i = 0; i < postTests.length; i++){
            const postTest = postTests[i];
            postTestObj[postTest.series] = postTest.value;
            tooltipVals.set(postTest.series+'postTest',{value: postTest.value, name:postTest.name, hoverDisplay: postTest.hoverDisplay, answered: postTest.answered})
        }

        dataPoints.push(preTestObj);
        dataPoints.push(postTestObj);
        populateLines();

        return dataPoints;
    }

    const populateLines = ()=>{

        const lineArr = [],
            lineColMean = [];

        lineColMap.forEach((value, key)=>{
            if(value.data.userId === 0){
                lineColMean.push(<Line strokeDasharray={'4 5'} isAnimationActive={false} key={key} type="linear" dot={<CustomDot data={value.data} />} strokeWidth={showAverageLine ? value.lineWidth : 0} activeDot={<CustomActiveDot data={value.data} />} dataKey={key} stroke={value.stroke} />)
            }else{
                lineArr.push(<Line isAnimationActive={false} key={key} type="linear" dot={<CustomDot data={value.data} />} strokeWidth={showTrendLines ? value.lineWidth : 0} activeDot={<CustomActiveDot data={value.data} />} dataKey={key} stroke={value.stroke} />)
            }
        })

        setLineColArr(lineArr);
        setLineColMean(lineColMean);

    }

    useEffect(()=>{
        if(rechartDataPoints.length > 0){
            populateLines();
        }
    },[showAverageLine, showTrendLines]);

    const CustomDot = (props)=>{
        return (<GraphDot {...props} r={5}  />);
    }

    const CustomActiveDot = (props)=>{
        if(props.payload.isDummy){
            return null;
        }
        return (<GraphDot {...props} r={6}  />);
    }

    const GraphDot = (props)=>{
        const {cx, cy, stroke, payload, value, r, strokeWidth, dataKey, data} = props;

        if (payload.isDummy){
            return null;
        }

        let radius = r;
        let fill = stroke;

        if(dataKey === GRID_ENUMS.USER+datasetMeanId){
            fill = ChartLineColor.MEAN;
            if(value === undefined){
                radius = 0;
            }
        }else if(dataKey === GRID_ENUMS.USER+minAndMaxId){
            // return {r:0, fillOpacity:0, opacity:0, strokeOpacity:0};
            radius = 0;
        }else{
            // return {fill:ChartLineColor.DEFAULT,padding:0};
            fill = ChartLineColor.DEFAULT;
        }


        if (studentSeriesMap[dataKey] || (studentSeriesMap[dataKey] !== undefined && isNaN(studentSeriesMap[dataKey].differenceValue))) {
            if (payload.test === preTest) {
                if (!studentSeriesMap[dataKey].preTestAnswered) {
                    fill = ChartLineColor.UNANSWERED;
                }
            } else if (payload.test === postTest) {
                if (!studentSeriesMap[dataKey].postTestAnswered) {
                    fill = ChartLineColor.UNANSWERED;
                }
            }
        }

        return(
            <circle
                cx={cx}
                cy={cy}
                r={radius}
                strokeWidth={1}
                stroke={'#bfbfbf'}
                fill={fill}
            />
    )
    }

    const addAdaptiveDataToStudent = (map,result,testType)=>{
        if(map.hasOwnProperty(result.userId)) {
            if (map[result.userId].hasOwnProperty(testType)) {
                map[result.userId][testType].testMark += result.mark !== undefined ? result.mark : 0;
                map[result.userId][testType].testMaxMark += result.adaptiveMaxMark !== 0 ? result.adaptiveMaxMark : result.maxMark;
                map[result.userId][testType].testPercentVal = calculateRoundPercent(map[result.userId][testType].testMark, map[result.userId][testType].testMaxMark);
                map[result.userId][testType].answered = true;
            }
        }
    }

    const addDataToStudent = (map,result,testMaxMark,testType)=>{
        if(map.hasOwnProperty(result.userId)){
            if(map[result.userId].hasOwnProperty(testType)){
                map[result.userId][testType].testMark += result.mark !== undefined ? result.mark : 0;
                map[result.userId][testType].testMaxMark = testMaxMark;
                map[result.userId][testType].testPercentVal = calculateRoundPercent(map[result.userId][testType].testMark, map[result.userId][testType].testMaxMark);
                map[result.userId][testType].answered = true;
            }
        }

    }

const CustomTooltip = (props) => {
    const { active, payload } = props;

    payload.sort((a, b) =>{
        const a2 = tooltipVals.get(a.name+a.payload.testType),
            b2 = tooltipVals.get(b.name+b.payload.testType);
        if(b2.value === a2.value){
            return Number(b2.answered) - Number(a2.answered);
        }else{
            return b2.value - a2.value;
        }
    });

        return (
          <Card rounded={false} style={{background:'#ffffff'}}>
              {(payload[0] && payload[0].payload && payload[0].payload.test) && <div className={'ch-bold margin-b--s'} style={{borderBottom: '1px solid lightgrey'}}>{payload[0].payload.test}</div>}
              <div className={'flexColumn smallGap'}>
                  {payload.map((load)=>{
                      if(load.name && tooltipVals.has(load.name+load.payload.testType) && tooltipVals.get(load.name+load.payload.testType).hasOwnProperty('value')){
                          return <span key={load.name} style={{color: load.name === 'user0' ? ChartLineColor.MEAN :''}} className={load.name === 'user0' ? 'ch-bold' : ''}>{tooltipVals.get(load.name+load.payload.testType).name}: {tooltipVals.get(load.name+load.payload.testType).hoverDisplay}</span>
                      }
                  })}
              </div>
          </Card>
        );
    };


    const onExportCSV = useCallback(() => {
        gridRef.current.api.exportDataAsCsv({fileName:REPORT_NAME});
    }, []);

    const onExportPDF = useCallback(() => {
        printDoc(defaultPDFParams,gridRef.current.api,REPORT_NAME);
    }, []);

    const getToolBar = () => (
        <div className="export-toolbar genBtn-blk">
            <button type="button" className="btn btn-default excel genBtn smallBtn"
                    onClick={onExportCSV}
            >Export CSV <FileDownloadIcon/>
            </button>
            <button type="button" className="btn btn-default reset genBtn smallBtn"
                    onClick={onExportPDF}
            >Export PDF <FileDownloadIcon/>
            </button>
        </div>
    );

    return(
        <div className="comparison-view flexColumn">
        {!isLoading &&
            <>
            {chartData != null &&
                <div className={'chart'}>
                    <h5>{preTest} vs {postTest} growth</h5>
                    <div style={{marginTop: '30px', display: 'flex', gap: '10px'}}>
                        <div style={{width:'80%',height:'400px'}}>
                            <ResponsiveContainer width={'100%'} height={'100%'}>
                            <LineChart data={rechartDataPoints}>
                                <XAxis dataKey="test"  padding={{ left: 100, right: 100 }} />
                                <YAxis ticks={[0,10,20,30,40,50,60,70,80,90,100]} tickFormatter={(value) => `${value}%`} interval={0} />
                                <Tooltip content={CustomTooltip} />
                                {lineColArr}
                                {showAverageLine && lineColMean}
                            </LineChart>
                            </ResponsiveContainer>
                        </div>
                        <LineChartLegend showAsCard={true} cardColor={'#FAFAFB'} data={comparisonLegendData} style={{width: '20%', justifyContent: 'center', padding: '8px', marginTop:'-20px'}}/>
                    </div>
                </div>
            }
            {graphData != null &&
                <div className={'graph'}>
                    <div className={'data-set-header'}>
                        <h5>Data-set</h5> {getToolBar()}
                    </div>
                    <div className="ag-theme-balham ch-general-table-ag-grid"
                         style={{height: (rowHeight * graphData.length + headerHeight + 10) + 'px'}}>
                        <AgGridReact
                            ref={gridRef}
                            suppressPropertyNamesCheck={true}
                            suppressHorizontalScroll={true}
                            suppressRowTransform={true}
                            rowData={graphData}
                            columnDefs={columnDefs}
                            defaultColDef={COL_DEFS.defaultColDef}
                            onGridReady={onGridReady}
                            // onRowClicked={handleRowClick}
                            rowHeight={rowHeight}
                        />
                    </div>
                </div>
            }
            </>
        }
    </div>
            )
        }
export default ComparisonView;
